import { useState, useRef } from 'react';
import { Btn, Input, Textarea, useT } from './UI';
import { Scanner } from './Misc';

const LEET = { a:'4', e:'3', i:'1', o:'0', s:'$', t:'7', l:'1', b:'8', g:'9', z:'2' };
const SIMBOLOS = ['!', '@', '#', '$', '*', '&', '?'];

function toLeet(str) {
  return str.split('').map(c => LEET[c.toLowerCase()] || c).join('');
}

function GeneradorContrasena() {
  const theme = useT();
  const [input,  setInput]  = useState('');
  const [result, setResult] = useState('');
  const [copied, setCopied] = useState(false);
  const [simbolo, setSimbolo] = useState('!');

  function generar() {
    if (!input.trim()) return;
    const leet = toLeet(input.trim());
    // Primera letra mayúscula del resultado
    const pass = leet.charAt(0).toUpperCase() + leet.slice(1) + simbolo;
    setResult(pass);
    setCopied(false);
  }

  function copiar() {
    navigator.clipboard.writeText(result).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div style={{ background:theme.surface, borderRadius:12, border:`1px solid ${theme.border}`, padding:24 }}>
      <div style={{ fontSize:15, fontWeight:700, color:theme.ink, marginBottom:4 }}>🔐 Contraseña Haxor</div>
      <div style={{ fontSize:12, color:theme.faint, marginBottom:16 }}>Convierte una palabra a lenguaje haxor y añade un símbolo al final</div>

      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        <Input label="Palabra base" value={input} onChange={setInput} placeholder="ej: marcos" onKeyDown={e => e.key==='Enter' && generar()} />

        <div>
          <div style={{ fontSize:12, color:theme.muted, marginBottom:6, fontWeight:500 }}>Símbolo final</div>
          <div style={{ display:'flex', gap:6 }}>
            {SIMBOLOS.map(s => (
              <button key={s} onClick={() => setSimbolo(s)} style={{
                width:36, height:36, border: simbolo===s ? `2px solid ${theme.primary}` : `1px solid ${theme.border}`,
                borderRadius:8, background: simbolo===s ? theme.primaryBg : theme.surface2,
                color: simbolo===s ? theme.primary : theme.body, fontSize:16, cursor:'pointer', fontWeight:700,
              }}>{s}</button>
            ))}
          </div>
        </div>

        <Btn variant="primary" onClick={generar} style={{ alignSelf:'flex-start' }}>Generar</Btn>

        {result && (
          <div style={{ marginTop:4 }}>
            <div style={{ fontSize:12, color:theme.muted, marginBottom:6, fontWeight:500 }}>Contraseña generada</div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{
                flex:1, background:'#f0f4ff', border:'1px solid #c7d7fd', borderRadius:8,
                padding:'10px 14px', fontFamily:'monospace', fontSize:20, fontWeight:700,
                color:'#1a56db', letterSpacing:2,
              }}>{result}</div>
              <Btn variant={copied ? 'success' : 'outline'} onClick={copiar}>
                {copied ? '✓ Copiada' : '📋 Copiar'}
              </Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EtiquetaLibre() {
  const theme = useT();
  const [texto, setTexto] = useState('');
  const [copias, setCopias] = useState(1);
  const [imprimiendo, setImprimiendo] = useState(false);

  function imprimir() {
    if (!texto.trim()) return;
    setImprimiendo(true);
    fetch('/api/etiqueta-ql', { method:'POST', headers:{ Authorization:`Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type':'application/json' },
      body: JSON.stringify({ tipo:'texto-libre', desc: texto.trim(), copias }) })
      .then(r=>r.json()).then(d=>{ if(!d.ok) alert('Error: '+d.error); })
      .catch(e=>alert('Error: '+e.message))
      .finally(()=>setImprimiendo(false));
  }

  return (
    <div style={{ background:theme.surface, borderRadius:12, border:`1px solid ${theme.border}`, padding:24 }}>
      <div style={{ fontSize:15, fontWeight:700, color:theme.ink, marginBottom:4 }}>🏷️ Etiqueta Brother QL</div>
      <div style={{ fontSize:12, color:theme.faint, marginBottom:16 }}>Imprime "ROOT" + un texto libre (etiqueta corta, ~5cm; si el texto no cabe se parte en varias líneas)</div>

      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        <Textarea label="Texto" value={texto} onChange={setTexto} placeholder="ej: Cable HDMI 2m" rows={3} />
        <div style={{ display:'flex', gap:10, alignItems:'flex-end' }}>
          <Input label="Copias" value={copias} onChange={v=>setCopias(Math.max(1, parseInt(v)||1))} type="number" min="1" style={{ width:80 }} />
          <Btn variant="primary" disabled={imprimiendo || !texto.trim()} onClick={imprimir}>
            {imprimiendo ? 'Imprimiendo…' : 'Imprimir etiqueta'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function NotaImprimible() {
  const theme = useT();
  const editorRef = useRef(null);
  const fileRef = useRef(null);
  const [copias, setCopias] = useState(1);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [vacio, setVacio] = useState(true);

  function actualizarVacio() {
    const el = editorRef.current;
    setVacio(!el.textContent.trim() && !el.querySelector('img'));
  }

  function exec(cmd, val) {
    editorRef.current.focus();
    document.execCommand(cmd, false, val);
    actualizarVacio();
  }

  function limpiar() {
    editorRef.current.innerHTML = '';
    setVacio(true);
  }

  function subirImagen(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => exec('insertHTML', `<img src="${reader.result}" style="width:240px">`);
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  function imprimir() {
    const html = editorRef.current.innerHTML;
    if (!html.trim()) return;
    setImprimiendo(true);
    fetch('/api/imprimir-nota', { method:'POST', headers:{ Authorization:`Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type':'application/json' },
      body: JSON.stringify({ html, copias }) })
      .then(r=>r.json()).then(d=>{ if(!d.ok) alert('Error: '+d.error); })
      .catch(e=>alert('Error: '+e.message))
      .finally(()=>setImprimiendo(false));
  }

  return (
    <div style={{ background:theme.surface, borderRadius:12, border:`1px solid ${theme.border}`, padding:24 }}>
      <div style={{ fontSize:15, fontWeight:700, color:theme.ink, marginBottom:4 }}>📝 Nota para imprimir</div>
      <div style={{ fontSize:12, color:theme.faint, marginBottom:16 }}>Escribe una nota y mándala a la térmica de notas (POS-58)</div>

      <div style={{ display:'flex', gap:6, marginBottom:8, flexWrap:'wrap' }}>
        <Btn variant="outline" onClick={() => exec('bold')}>Negrita</Btn>
        <Btn variant="outline" onClick={() => exec('fontSize', '3')}>Normal</Btn>
        <Btn variant="outline" onClick={() => exec('fontSize', '6')}>Grande</Btn>
        <Btn variant="outline" onClick={() => exec('justifyLeft')}>Izq.</Btn>
        <Btn variant="outline" onClick={() => exec('justifyCenter')}>Centro</Btn>
        <Btn variant="outline" onClick={() => exec('insertHTML', '<hr style="border:none;border-top:4px solid #000;margin:8px 0">')}>➖ Línea</Btn>
        <Btn variant="outline" onClick={() => fileRef.current.click()}>🖼️ Imagen</Btn>
        <input ref={fileRef} type="file" accept="image/*" onChange={subirImagen} style={{ display:'none' }} />
        <Btn variant="outline" onClick={limpiar}>🗑️ Limpiar</Btn>
      </div>

      <div
        ref={editorRef}
        contentEditable
        onInput={actualizarVacio}
        suppressContentEditableWarning
        style={{ minHeight:140, background:'#fff', color:'#000', border:`1px solid ${theme.border}`, borderRadius:8, padding:'10px 12px', fontSize:15, outline:'none', marginBottom:12 }}
      />

      <div style={{ display:'flex', gap:10, alignItems:'flex-end' }}>
        <Input label="Copias" value={copias} onChange={v=>setCopias(Math.max(1, parseInt(v)||1))} type="number" min="1" style={{ width:80 }} />
        <Btn variant="primary" disabled={imprimiendo || vacio} onClick={imprimir}>
          {imprimiendo ? 'Imprimiendo…' : 'Imprimir nota'}
        </Btn>
      </div>
    </div>
  );
}

export default function Utilidades() {
  const theme = useT();
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:24, alignItems:'start' }}>
      <GeneradorContrasena />
      <EtiquetaLibre />
      <NotaImprimible />
      <div>
        <div style={{ fontSize:15, fontWeight:700, color:theme.ink, marginBottom:4 }}>🖨️ Escáner de documentos</div>
        <div style={{ fontSize:12, color:theme.faint, marginBottom:16 }}>Escanea con la Brother DCP-7065DN y descarga el PDF</div>
        <Scanner />
      </div>
    </div>
  );
}
